import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

type Props = {
  label: ReactNode;
  text: ReactNode;
  isLast?: boolean;
  layout?: 'columns' | 'list';
  /**
   * Column headers, repeated inline in each row on mobile so the stacked
   * layout keeps its "which column is this" cue. Skipped on desktop where the
   * grid header row is already visible.
   */
  leftColumnTitle?: string;
  rightColumnTitle?: string;
  /**
   * Colour of the row arrow. The default matches the ServiceListAccordion
   * "columns" and "list" layouts (`#000000` for columns, `#7c5cff` for list
   * — callers already relying on the previous look pass nothing).
   */
  arrowColor?: string;
  /**
   * Typography override for the label cell. Defaults to the ServiceListAccordion
   * look (`text-[18px] font-semibold leading-[1.4] text-black`). Callers that
   * feed rich-text into the label (e.g. LabeledStatements) can pass a
   * non-bold class so `<strong>` marks inside the field drive the weight.
   */
  labelClassName?: string;
  /**
   * Typography override for the text cell. Defaults to the ServiceListAccordion
   * look (`text-[16px] font-light leading-[1.5] text-black md:text-[18px]`).
   * Callers pass this to swap the text colour (e.g. `text-[#041118]`) without
   * having to fork the shared component.
   */
  textClassName?: string;
};

const DEFAULT_LABEL_TYPOGRAPHY =
  'text-[18px] font-semibold leading-[1.4] text-black';

const DEFAULT_TEXT_TYPOGRAPHY =
  'text-[16px] font-light leading-[1.5] text-black md:text-[18px]';

const EYEBROW_CLASS =
  'text-[11px] font-medium uppercase leading-[1.4] tracking-[0.08em] text-black/50 md:hidden';

export function DetailRow({
  label,
  text,
  isLast = false,
  layout = 'columns',
  leftColumnTitle = '',
  rightColumnTitle = '',
  arrowColor,
  labelClassName = DEFAULT_LABEL_TYPOGRAPHY,
  textClassName = DEFAULT_TEXT_TYPOGRAPHY,
}: Props) {
  if (!label && !text) {
    return null;
  }

  if (layout === 'list') {
    const borderClass = isLast ? '' : 'border-b border-[#d9d9d9]';
    const listArrowColor = arrowColor ?? '#7c5cff';

    return (
      <div className={`flex items-center gap-3 py-4 ${borderClass}`}>
        <span
          aria-hidden
          className="shrink-0 text-[16px] font-semibold leading-none md:text-[18px]"
          style={{ color: listArrowColor }}
        >
          →
        </span>
        <p className="min-w-0 text-[16px] font-normal leading-[1.5] text-black md:text-[18px]">
          {text}
        </p>
      </div>
    );
  }

  const columnsArrowColor = arrowColor ?? '#000000';

  // Desktop (md+): the wrapper collapses to `contents` so the three children sit
  // directly in the panel's 3-column grid — unchanged from the original layout.
  // Mobile: the wrapper stays a block, each row reads as a stacked card with an
  // eyebrow-label above the left value and above the description.
  const desktopBorder = isLast ? '' : 'md:border-b md:border-[#d9d9d9]';
  const mobileBorder = isLast ? '' : 'border-b border-[#d9d9d9]';

  return (
    <div className={`flex flex-col gap-2 py-8 md:contents ${mobileBorder}`}>
      {leftColumnTitle ? (
        <span className={EYEBROW_CLASS}>{leftColumnTitle}</span>
      ) : null}

      <p
        className={cn(
          'min-w-0 md:py-4 md:content-center',
          labelClassName,
          desktopBorder,
        )}
      >
        {label}
      </p>

      {rightColumnTitle ? (
        <span className={`${EYEBROW_CLASS} mt-4`}>{rightColumnTitle}</span>
      ) : null}

      <span
        aria-hidden
        className="hidden py-4 text-[16px] font-semibold leading-none md:flex md:items-center md:justify-center"
        style={{ color: columnsArrowColor }}
      >
        →
      </span>

      <p className={cn('min-w-0 md:py-4', textClassName, desktopBorder)}>
        {text}
      </p>
    </div>
  );
}
